import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, type SearchResponse } from "../../lib/api";
import { splitHighlight } from "../../lib/format";
import {
  CategoryChip,
  Empty,
  ErrorNote,
  PageHeader,
  Spinner,
  StatusPill,
} from "../../components/ui";

export function SearchScreen() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const query = params.get("q") ?? "";
  const [draft, setDraft] = useState(query);
  const [category, setCategory] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => setDraft(query), [query]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["search", query],
    queryFn: () => api.get<SearchResponse>("/search", { q: query, limit: 40 }),
    enabled: query.trim().length > 0,
  });

  // Facets filter the results already on screen rather than re-querying: the
  // counts came from the same result set, so filtering client-side keeps them
  // consistent with what is shown.
  const hits = (data?.hits ?? []).filter(
    (hit) =>
      (!category || hit.category === category) &&
      (!status || hit.status === status),
  );

  return (
    <>
      <PageHeader
        eyebrow="Hybrid search"
        title={query ? `Results for “${query}”` : "Search the compendium"}
        subtitle={
          data
            ? `${data.documentCount} document${data.documentCount === 1 ? "" : "s"} matched · keyword and vector search combined`
            : "Exact references and paraphrases both resolve"
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && draft.trim()) {
              setParams({ q: draft.trim() });
            }
          }}
          placeholder="Refine your search…"
          aria-label="Refine your search"
          className="field max-w-lg"
        />
        <button
          className="btn-primary"
          onClick={() => draft.trim() && setParams({ q: draft.trim() })}
        >
          Search
        </button>
        {query && (
          <button
            className="btn-ghost"
            onClick={() => navigate(`/assistant?q=${encodeURIComponent(query)}`)}
          >
            Ask Compendium Discover instead
          </button>
        )}
      </div>

      {data && (data.categories.length > 0 || data.statuses.length > 0) && (
        <div className="mb-4 space-y-2">
          <FacetRow
            label="Category"
            options={data.categories}
            active={category}
            onChange={setCategory}
          />
          <FacetRow
            label="Status"
            options={data.statuses}
            active={status}
            onChange={setStatus}
          />
        </div>
      )}

      {isLoading && <Spinner label="Searching…" />}
      {error && <ErrorNote error={error} />}

      {/* Nothing in the library contains these words, so every result below is
          the vector index's nearest neighbour -- and it always returns those,
          however far away they are. Saying so is the only honest option: the
          distances themselves do not separate a real paraphrase from an
          unanswerable query, so the reader has to make that call. */}
      {data && hits.length > 0 && !data.lexicalMatch && (
        <div
          className="mb-3 flex items-start gap-2 rounded-card border border-amber-line bg-amber-wash px-3 py-2.5 text-base+ leading-[1.55] text-amber-deep"
          role="status"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="mt-[2px] shrink-0"
            aria-hidden
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v5M12 16.5v.01" />
          </svg>
          <span>
            <strong>No document contains these words.</strong> The passages
            below are the closest in meaning, which may or may not be what you
            are after. Try a document reference, a term used in the documents
            themselves, or{" "}
            <Link
              to={`/assistant?q=${encodeURIComponent(query)}`}
              className="font-bold underline underline-offset-2"
            >
              ask Compendium Discover
            </Link>
            , which will tell you outright if the compendium cannot answer.
          </span>
        </div>
      )}

      {data && hits.length === 0 && (
        <div className="card">
          <Empty
            title="Nothing matched that term"
            detail="Try fewer words, a document reference such as SGN-OT-PRC-114, or ask Compendium Discover a question in plain English."
          />
        </div>
      )}

      <ul className="space-y-2">
        {hits.map((hit) => {
          const fragments = splitHighlight(hit.highlight);
          return (
            <li key={hit.chunkId}>
              <Link
                to={`/documents/${hit.documentId}?page=${hit.pageFrom}`}
                className="block rounded-card border border-line bg-surface p-4 no-underline transition-colors hover:border-ink"
              >
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <span className="text-md font-bold text-ink">{hit.title}</span>
                  <StatusPill status={hit.status} label={hit.statusLabel} />
                  <CategoryChip label={hit.categoryLabel} />
                  {/* The percentage is relative to the best hit for this
                      query, so it only means anything when something actually
                      matched the words. For a vector-only hit it would read as
                      a confidence the ranking cannot support. */}
                  <span className="ml-auto text-2xs font-bold text-muted-soft">
                    {hit.matchedKeyword
                      ? `${hit.matchPercent}% match`
                      : "related by meaning"}
                  </span>
                </div>

                <div className="mb-2 text-2xs text-muted-soft">
                  {hit.docNo} · {hit.versionLabel} ·{" "}
                  {hit.pageFrom === hit.pageTo
                    ? `page ${hit.pageFrom}`
                    : `pages ${hit.pageFrom}–${hit.pageTo}`}
                  {hit.sectionHeading && ` · ${hit.sectionHeading}`}
                </div>

                <p className="text-sm leading-relaxed text-muted-strong">
                  {fragments.length > 0
                    ? fragments.map((fragment, index) =>
                        fragment.mark ? (
                          <mark
                            key={index}
                            className="rounded-[2px] bg-[#FCF6D2] px-0.5 text-ink"
                          >
                            {fragment.text}
                          </mark>
                        ) : (
                          <span key={index}>{fragment.text}</span>
                        ),
                      )
                    : hit.snippet}
                </p>

                <div className="mt-2 flex gap-1.5">
                  {hit.matchedKeyword && (
                    <span className="rounded-chip bg-surface-sunken px-1.5 py-[2px] text-2xs font-bold text-muted">
                      keyword
                    </span>
                  )}
                  {hit.matchedVector && (
                    <span className="rounded-chip bg-surface-sunken px-1.5 py-[2px] text-2xs font-bold text-muted">
                      meaning
                    </span>
                  )}
                  {hit.fromDiagram && (
                    <span
                      className="rounded-chip bg-clay-wash px-1.5 py-[2px] text-2xs font-bold text-clay-deep"
                      title="Part of this passage was read out of a picture by OCR, so it is only as good as the transcription."
                    >
                      diagram text
                    </span>
                  )}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function FacetRow({
  label,
  options,
  active,
  onChange,
}: {
  label: string;
  options: { value: string; label: string; count: number }[];
  active: string | null;
  onChange: (value: string | null) => void;
}) {
  if (options.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="eyebrow w-16">{label}</span>
      <button
        onClick={() => onChange(null)}
        className={`rounded-full px-2.5 py-1 text-sm font-bold ${
          active === null
            ? "bg-ink text-pearl"
            : "border border-line bg-surface text-muted-strong hover:bg-surface-hover"
        }`}
      >
        All
      </button>
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(active === option.value ? null : option.value)}
          className={`rounded-full px-2.5 py-1 text-sm font-bold ${
            active === option.value
              ? "bg-ink text-pearl"
              : "border border-line bg-surface text-muted-strong hover:bg-surface-hover"
          }`}
        >
          {option.label}
          <span className="ml-1.5 opacity-60">{option.count}</span>
        </button>
      ))}
    </div>
  );
}
